import React from "react";
import { matchPath, useLocation } from "react-router-dom";
import { HeaderSection } from "components/layout/HeaderSection";
import { PageContainer } from "components/layout/PageContainer";
import { useAuth } from "contexts/AuthContext";
import { cn } from "lib/utils";
import { ROUTE_GROUPS } from "../../routes/config";

interface NavigationLayoutProps {
  children: React.ReactNode;
  // `full` is for internal admin workspaces, which use the whole screen width.
  contentWidth?: 'standard' | 'narrow' | 'full';
}

const HIDE_HEADER_FOR_PUBLIC_PATHS = new Set(["/terms", "/privacy"]);
const HIDE_HEADER_ROUTE_PATTERNS = ["/meetings/:meetingId"];
const KNOWN_CLIENT_ROUTE_PATHS = ROUTE_GROUPS
  .flatMap(({ routes }) => routes.map((route) => route.path))
  .filter((path) => path !== '*');

const isKnownClientPath = (pathname: string) =>
  KNOWN_CLIENT_ROUTE_PATHS.some((path) => matchPath({ path, end: true }, pathname));

const CONTENT_MAX_WIDTH = {
  standard: 'screen-xl',
  narrow: '4xl',
  full: 'none',
} as const;

export const NavigationLayout: React.FC<NavigationLayoutProps> = ({
  children,
  contentWidth = 'standard',
}) => {
  const location = useLocation();
  const { isAuthenticated } = useAuth();
  const isPublicLegalPage = HIDE_HEADER_FOR_PUBLIC_PATHS.has(location.pathname);
  const isFullscreenRoute = HIDE_HEADER_ROUTE_PATTERNS.some((path) =>
    matchPath({ path, end: true }, location.pathname),
  );
  const isLoggedOutUnknownPath = !isAuthenticated && !isKnownClientPath(location.pathname);
  const showHeader = !isFullscreenRoute && (isAuthenticated || (!isPublicLegalPage && !isLoggedOutUnknownPath));
  const header = (
    <PageContainer className="pt-2 sm:pt-4 lg:pt-6" data-testid="navigation-layout-header">
      <HeaderSection />
    </PageContainer>
  );

  return (
    <div className="min-h-screen w-full">
      <div className="flex flex-col">
        {showHeader && (
          <header>
            {/* Full-width pages skip ThemedApp's padded screen-xl column, so restore it around the
                header to keep it identical to the live app. */}
            {contentWidth === 'full' ? <PageContainer>{header}</PageContainer> : header}
          </header>
        )}

        <main
          className={cn(
            "flex-1 pt-4 sm:pt-6",
            showHeader ? "pb-20 lg:pb-2" : "pb-2",
          )}
          data-testid="navigation-layout-main"
        >
          <PageContainer
            data-testid="navigation-layout-content"
            maxWidth={CONTENT_MAX_WIDTH[contentWidth]}
          >
            {children}
          </PageContainer>
        </main>
      </div>
    </div>
  );
};
